import { headersDeGeoDoPortal } from '@/features/portal/utils/geoDoPortal';
import { portalGet, portalPost } from '@/features/portal/utils/portalRequest';

import type {
  ArbitragemDetalhe,
  ArbitragemPendencia,
  ArbitragemPendenciaResolucao,
  ArbitragemPendenciaResolucaoResultado,
} from '../types/arbitragemDetalheTypes';
import {
  buscarArbitragemMock,
  ehArbitragemMock,
  listarArbitragensMock,
  listarNotificacoesMock,
  portalMockArbitragensAtivo,
  resolverPendenciaMock,
  submeterManifestacaoMock,
  uploadAnexoMock,
} from '../utils/arbitragensMock';
import { montarArbitragemDetalhe } from '../utils/montarArbitragemDetalhe';

import type {
  CitacaoArbitralAuthChannel,
  CitacaoArbitralDetail,
  CitacaoArbitralKycInitiateResponse,
  CitacaoArbitralKycStatusResponse,
  CitacaoArbitralKycUpdateContactResponse,
  CitacaoArbitralListItem,
  CitacaoArbitralMagicLinkConsumeResponse,
  CitacaoArbitralMagicLinkStatusResponse,
  CitacaoArbitralRequestFallbackResponse,
  CitacaoArbitralRequestMagicLinkResponse,
  CitacaoArbitralSubmitPayload,
  CitacaoArbitralSubmitResponse,
  CitacaoArbitralUploadResult,
  CitacaoArbitralValidateHashResponse,
  ConviteAdvogadoEmitirPayload,
  ConviteAdvogadoEmitirResponse,
  PortalFeedResponse,
  SolicitarTrocaEspecialistaPayload,
  SolicitarTrocaEspecialistaResponse,
} from '../types/portalArbitralTypes';

const ARBITRAL_PORTAL_BASE = '/arbitral/portal';

async function unwrap<T>(response: Response, fallbackMessage: string): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: { message: fallbackMessage } }));
    throw new Error(errorData.error?.message || fallbackMessage);
  }
  const raw = await response.json();
  const payload = (raw as { data?: T }).data ?? (raw as T);
  return payload;
}

export const citacaoArbitralService = {
  convidarAdvogado: async (
    parteArbitralId: string,
    payload: ConviteAdvogadoEmitirPayload,
  ): Promise<ConviteAdvogadoEmitirResponse> => {
    const response = await portalPost(
      `${ARBITRAL_PORTAL_BASE}/citacoes/${parteArbitralId}/convites-advogado`,
      payload,
    );
    return unwrap<ConviteAdvogadoEmitirResponse>(response, 'Não foi possível enviar o convite');
  },

  solicitarTrocaEspecialista: async (
    parteArbitralId: string,
    payload: SolicitarTrocaEspecialistaPayload,
  ): Promise<SolicitarTrocaEspecialistaResponse> => {
    const response = await portalPost(
      `${ARBITRAL_PORTAL_BASE}/citacoes/${parteArbitralId}/solicitar-troca-especialista`,
      payload,
    );
    return unwrap<SolicitarTrocaEspecialistaResponse>(
      response,
      'Não foi possível enviar a solicitação',
    );
  },

  validateOnboardingHash: async (hash: string): Promise<CitacaoArbitralValidateHashResponse> => {
    const response = await portalGet(
      `${ARBITRAL_PORTAL_BASE}/onboarding/validate/${hash}`,
      undefined,
      true,
    );
    return unwrap<CitacaoArbitralValidateHashResponse>(response, 'Erro ao validar link de citação');
  },

  requestMagicLink: async (
    hash: string,
    canal: CitacaoArbitralAuthChannel,
  ): Promise<CitacaoArbitralRequestMagicLinkResponse> => {
    const response = await portalPost(
      `${ARBITRAL_PORTAL_BASE}/onboarding/request-magic-link/${hash}`,
      { canal },
      true,
    );
    return unwrap<CitacaoArbitralRequestMagicLinkResponse>(
      response,
      'Erro ao solicitar link de acesso',
    );
  },

  consumeMagicLink: async (token: string): Promise<CitacaoArbitralMagicLinkConsumeResponse> => {
    const response = await portalPost(
      `${ARBITRAL_PORTAL_BASE}/auth/magic-link/${token}`,
      {},
      true,
      await headersDeGeoDoPortal(),
    );
    return unwrap<CitacaoArbitralMagicLinkConsumeResponse>(
      response,
      'Não foi possível validar o link de acesso',
    );
  },

  checkMagicLinkStatus: async (
    hash: string,
    pollToken: string,
  ): Promise<CitacaoArbitralMagicLinkStatusResponse> => {
    const response = await portalGet(
      `${ARBITRAL_PORTAL_BASE}/auth/magic-link-status/${hash}`,
      { pollToken },
      true,
    );
    return unwrap<CitacaoArbitralMagicLinkStatusResponse>(response, 'Erro ao verificar status');
  },

  requestFallback: async (
    hash: string,
    currentChannel: CitacaoArbitralAuthChannel,
  ): Promise<CitacaoArbitralRequestFallbackResponse> => {
    const response = await portalPost(
      `${ARBITRAL_PORTAL_BASE}/onboarding/fallback/${hash}`,
      { currentChannel },
      true,
    );
    return unwrap<CitacaoArbitralRequestFallbackResponse>(response, 'Erro ao tentar outro canal');
  },

  initiateKyc: async (hash: string): Promise<CitacaoArbitralKycInitiateResponse> => {
    const response = await portalPost(`${ARBITRAL_PORTAL_BASE}/kyc/initiate/${hash}`, {}, true);
    return unwrap<CitacaoArbitralKycInitiateResponse>(
      response,
      'Não foi possível iniciar a verificação facial',
    );
  },

  getKycStatus: async (verificationId: string): Promise<CitacaoArbitralKycStatusResponse> => {
    const response = await portalGet(
      `${ARBITRAL_PORTAL_BASE}/kyc/status/${verificationId}`,
      undefined,
      true,
    );
    return unwrap<CitacaoArbitralKycStatusResponse>(
      response,
      'Erro ao verificar a verificação facial',
    );
  },

  updateContactAfterKyc: async (
    hash: string,
    verificationId: string,
    novoTelefone: string,
  ): Promise<CitacaoArbitralKycUpdateContactResponse> => {
    const response = await portalPost(
      `${ARBITRAL_PORTAL_BASE}/kyc/update-contact/${hash}`,
      { verificationId, novoTelefone },
      true,
    );
    return unwrap<CitacaoArbitralKycUpdateContactResponse>(response, 'Erro ao atualizar contato');
  },

  listCitacoes: async (): Promise<CitacaoArbitralListItem[]> => {
    const response = await portalGet(`${ARBITRAL_PORTAL_BASE}/citacoes`);
    return unwrap<CitacaoArbitralListItem[]>(response, 'Erro ao listar citações');
  },

  getDetail: async (id: string): Promise<CitacaoArbitralDetail> => {
    const arbitragemMock = portalMockArbitragensAtivo() ? buscarArbitragemMock(id) : null;
    if (arbitragemMock) {
      return arbitragemMock;
    }

    const response = await portalGet(
      `${ARBITRAL_PORTAL_BASE}/citacoes/${id}`,
      undefined,
      false,
      await headersDeGeoDoPortal(),
    );
    const detalhe = await unwrap<CitacaoArbitralDetail>(
      response,
      'Erro ao obter detalhe da citação',
    );
    if (typeof detalhe?.id !== 'string' || detalhe.id.trim() === '') {
      throw new Error('Citação indisponível.');
    }
    return detalhe;
  },

  getArbitragemDetalhe: async (id: string): Promise<ArbitragemDetalhe> => {
    const arbitragemMock = portalMockArbitragensAtivo() ? buscarArbitragemMock(id) : null;
    if (arbitragemMock) {
      return montarArbitragemDetalhe(arbitragemMock);
    }

    const citacao = await citacaoArbitralService.getDetail(id);
    return montarArbitragemDetalhe(citacao);
  },

  resolverPendencia: async (
    id: string,
    pendencia: ArbitragemPendencia,
    resolucao: ArbitragemPendenciaResolucao,
  ): Promise<ArbitragemPendenciaResolucaoResultado> => {
    if (ehArbitragemMock(id)) {
      return resolverPendenciaMock(pendencia, resolucao);
    }

    const formData = new FormData();
    formData.append('resposta', resolucao.texto ?? '');
    resolucao.arquivos.forEach((arquivo) => formData.append('arquivos', arquivo));

    const response = await portalPost(
      `${ARBITRAL_PORTAL_BASE}/citacoes/${id}/pendencias/${pendencia.id}/resolver`,
      formData,
    );
    return unwrap<ArbitragemPendenciaResolucaoResultado>(response, 'Erro ao resolver pendência');
  },

  submeterManifestacao: async (
    id: string,
    payload: CitacaoArbitralSubmitPayload,
  ): Promise<CitacaoArbitralSubmitResponse> => {
    if (ehArbitragemMock(id)) {
      return submeterManifestacaoMock(payload);
    }

    const response = await portalPost(`${ARBITRAL_PORTAL_BASE}/citacoes/${id}/responder`, payload);
    return unwrap<CitacaoArbitralSubmitResponse>(response, 'Erro ao submeter manifestação');
  },

  uploadAnexo: async (id: string, file: File): Promise<CitacaoArbitralUploadResult> => {
    if (ehArbitragemMock(id)) {
      return uploadAnexoMock(file);
    }

    const formData = new FormData();
    formData.append('file', file);
    const response = await portalPost(`${ARBITRAL_PORTAL_BASE}/citacoes/${id}/upload`, formData);
    return unwrap<CitacaoArbitralUploadResult>(response, 'Erro ao fazer upload do arquivo');
  },

  getFeed: async (): Promise<PortalFeedResponse> => {
    if (!portalMockArbitragensAtivo()) {
      const response = await portalGet(`${ARBITRAL_PORTAL_BASE}/feed`);
      return unwrap<PortalFeedResponse>(response, 'Erro ao carregar portal');
    }

    const arbitragensMock = listarArbitragensMock();

    try {
      const response = await portalGet(`${ARBITRAL_PORTAL_BASE}/feed`);
      const feed = await unwrap<PortalFeedResponse>(response, 'Erro ao carregar portal');
      return { ...feed, arbitral: [...arbitragensMock, ...feed.arbitral] };
    } catch {
      return { arbitral: arbitragensMock, extrajudicial: { items: listarNotificacoesMock() } };
    }
  },
};
